import { useCallback, useEffect, useState } from 'react';

const VERSION_CHECK_INTERVAL_MS = 2 * 60 * 1000;
const VERSION_ENDPOINT = '/version.json';
const BUILD_VERSION = import.meta.env.REACT_APP_VERSION || '';

interface VersionCheckState {
    outdated: boolean;
    currentVersion: string;
    latestVersion: string;
}

const fetchLatestVersion = async (): Promise<string> => {
    const response = await fetch(`${VERSION_ENDPOINT}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch version: ${response.status}`);
    }

    const payload = await response.json();
    return payload.version;
};

const useVersionCheck = (): VersionCheckState => {
    const [state, setState] = useState<VersionCheckState>({
        outdated: false,
        currentVersion: BUILD_VERSION,
        latestVersion: BUILD_VERSION,
    });

    const checkVersion = useCallback(async () => {
        try {
            const serverVersion = await fetchLatestVersion();

            if (serverVersion && serverVersion !== BUILD_VERSION) {
                console.log(`Current version: ${BUILD_VERSION}, Latest version: ${serverVersion}`);

                setState({
                    outdated: true,
                    currentVersion: BUILD_VERSION,
                    latestVersion: serverVersion,
                });
            }
        } catch {
            // silently ignore fetch failures
        }
    }, []);

    useEffect(() => {
        checkVersion();

        const intervalId = setInterval(checkVersion, VERSION_CHECK_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [checkVersion]);

    return state;
};

export default useVersionCheck;
